import authService from '../services/authService';
import contractService from '../services/contractService';
import twilioService from '../services/twilioService';

export type LandingPath = '/dashboard' | '/onboarding';

/**
 * Where a signed-in account belongs: the dashboard once every onboarding step
 * is done, the onboarding otherwise. Used after a login and when someone who
 * is still signed in opens the login page.
 */
export async function resolveLandingPath(): Promise<LandingPath> {
  try {
    // Check all onboarding steps in parallel
    const [bizInfo, timeglobeKey, contractConfirm, avvConfirm, mandat, twilioNum] = await Promise.allSettled([
      authService.getBusinessInfo(),
      authService.getBusinessTimeglobeKey(),
      contractService.getContractConfirmation(),
      contractService.getDataProcessingConfirmation(),
      contractService.getLastschriftmandat(),
      twilioService.getLatestNumber(),
    ]);

    const biz = bizInfo.status === 'fulfilled' ? bizInfo.value : null;
    const key = timeglobeKey.status === 'fulfilled' ? timeglobeKey.value : null;
    const contract = contractConfirm.status === 'fulfilled' ? (contractConfirm.value as any) : null;
    const avv = avvConfirm.status === 'fulfilled' ? (avvConfirm.value as any) : null;
    const mandate = mandat.status === 'fulfilled' ? mandat.value : null;
    const twilio = twilioNum.status === 'fulfilled' ? (twilioNum.value as any) : null;

    // Step 1: API Key
    const hasApiKey = !!(key?.timeglobe_auth_key);

    // Step 2: Company Info
    const hasCompanyInfo = !!(
      biz?.business_name && biz?.street_address && biz?.city &&
      biz?.postal_code && biz?.country && biz?.contact_person &&
      biz?.email && biz?.phone_number && biz?.tax_id
    );

    // Step 3: Contracts (all 3 sub-steps)
    const hasAllContracts = !!(contract?.confirmed) && !!(avv?.confirmed) && !!mandate;

    // Step 4: Twilio Number (only required if wants_phone_agent is true or not yet decided)
    const hasTwilioNumber = !!(twilio?.phone_number);
    // Step 4 is complete if: number purchased, OR user explicitly doesn't want phone agent and doesn't need a reserved number
    const step4Complete = hasTwilioNumber || (biz?.wants_phone_agent === false && biz?.wants_whatsapp !== undefined);

    // Step 5: WhatsApp — complete if fully connected OR explicitly declined
    // Strict: whatsapp_number alone is not enough, waba_status must be "connected" too
    const whatsappFullyConnected = !!(biz?.whatsapp_number && biz?.waba_status === 'connected');
    const step5Complete = whatsappFullyConnected || biz?.wants_whatsapp === false;

    // All steps done → dashboard
    const onboarded = hasApiKey && hasCompanyInfo && hasAllContracts && step4Complete && step5Complete;
    return onboarded ? '/dashboard' : '/onboarding';
  } catch (err) {
    console.error('Error checking onboarding status:', err);
    return '/onboarding';
  }
}
